// GameApp: top-level orchestrator. Owns renderer/scene/camera/input/planes/bullets.
// Spawns a 3v3 match (player + 2 ally + 3 enemy), runs the combat loop, exposes
// HUD, kill feed, explode FX, and a basic match lifecycle.

import * as THREE from 'three';
import { Input, type InputMode } from './Input';
import { Plane, type Team } from '../plane/Plane';
import type { PlaneShape, PaperColor } from '../plane/PlaneMesh';
import { SkyArena } from '../world/SkyArena';
import { FollowCamera } from '../world/FollowCamera';
import { Bullet } from '../plane/Bullet';
import { BulletSystem } from '../plane/BulletSystem';
import { ExplodeFx } from '../world/ExplodeFx';
import { TrailFx } from '../world/TrailFx';
import { type AIController } from '../ai/AIController';
import { FullAIController } from '../ai/FullAIController';
import { type PersonalityId, PERSONALITY_IDS } from '../ai/personalities';
import { GameState } from './GameState';
import { HUD } from '../ui/HUD';
import { KillFeed } from '../ui/KillFeed';
import { DialogueSystem, type DialogueLine } from '../ai/Dialogue';
import { DialogueFeed } from '../ui/DialogueFeed';
import { VoiceRecognizer } from '../radio/VoiceRecognizer';
import { VoiceSynth } from '../radio/VoiceSynth';
import { RadioUI } from '../radio/RadioUI';
import { getPersonality } from '../ai/personalities';

export interface GameAppOptions {
  shape: PlaneShape;
  color: PaperColor;
  alliesPersonalities?: PersonalityId[];   // 2 picks
  enemiesPersonalities?: PersonalityId[];  // 3 picks
}

const DEFAULT_ALLIES: PersonalityId[] = ['veteran', 'rookie'];
const DEFAULT_ENEMIES: PersonalityId[] = ['pirate', 'sniper', 'maniac'];

const COLORS_FOR_TEAM: Record<Team, PaperColor[]> = {
  blue: ['white', 'kraft', 'blue'],
  red: ['red', 'newspaper', 'gold'],
};

const SHAPES_FOR_AI: PlaneShape[] = ['classic', 'dart', 'glider'];

interface PlaneEntry {
  plane: Plane;
  controller?: AIController;
}

function pickRandomAlive(planes: PlaneEntry[]): PlaneEntry | null {
  const live = planes.filter((e) => e.controller && e.plane.alive);
  if (live.length === 0) return null;
  return live[Math.floor(Math.random() * live.length)];
}

export class GameApp {
  renderer: THREE.WebGLRenderer;
  scene = new THREE.Scene();
  arena = new SkyArena();
  camera: FollowCamera;
  input = new Input();
  player: Plane;
  bullets: BulletSystem;
  fx: ExplodeFx;
  trails: TrailFx;
  state = new GameState();
  hud: HUD;
  killFeed: KillFeed;
  dialogue = new DialogueSystem();
  dialogueFeed: DialogueFeed;
  voiceRecognizer = new VoiceRecognizer();
  voiceSynth = new VoiceSynth();
  radioUI: RadioUI;

  planes: PlaneEntry[] = [];
  planeById = new Map<string, Plane>();
  controllerByCallsign = new Map<string, AIController>();
  private idleTimer = 0;
  private openingFired = false;

  private clock = new THREE.Clock();
  private rafId = 0;
  private modeBadge: HTMLDivElement | null = null;
  private fpsCounter: HTMLDivElement | null = null;
  private fpsLast = 0;
  private fpsFrames = 0;
  private nowSeconds = 0;
  private postScreen: HTMLDivElement | null = null;

  constructor(host: HTMLElement, opts: GameAppOptions) {
    this.scene.background = new THREE.Color(0x0a0a14);
    this.scene.fog = new THREE.Fog(0xc25e3a, 280, 700);
    this.scene.add(this.arena.root);

    this.renderer = new THREE.WebGLRenderer({ antialias: true, alpha: false });
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.setSize(window.innerWidth, window.innerHeight);
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    host.appendChild(this.renderer.domElement);

    this.camera = new FollowCamera(window.innerWidth / window.innerHeight);
    this.bullets = new BulletSystem(this.scene);
    this.fx = new ExplodeFx(this.scene);
    this.trails = new TrailFx(this.scene);

    // Spawn the 3v3 lineup.
    this.player = this.spawnPlayer(opts);
    const allies = opts.alliesPersonalities ?? DEFAULT_ALLIES;
    const enemies = opts.enemiesPersonalities ?? DEFAULT_ENEMIES;
    for (let i = 0; i < 2; i++) {
      this.spawnAI('blue', allies[i] ?? PERSONALITY_IDS[i % PERSONALITY_IDS.length], i, allies.length + 1);
    }
    for (let i = 0; i < 3; i++) {
      this.spawnAI('red', enemies[i] ?? PERSONALITY_IDS[(i + 3) % PERSONALITY_IDS.length], i, 3);
    }

    this.camera.follow(this.player, 0, true);
    this.hud = new HUD();
    this.killFeed = new KillFeed(this.hud.killFeedRoot);
    this.dialogueFeed = new DialogueFeed();
    this.dialogue.subscribe((line) => this.onDialogueLine(line));

    // Radio UI — push-to-talk + text fallback.
    const recognizerAvailable = this.voiceRecognizer.available;
    this.radioUI = new RadioUI({
      onPress: () => this.startListening(),
      onRelease: () => this.stopListening(),
      onTextSubmit: (text) => this.playerRadio(text, 'all'),
      onResume: () => this.voiceSynth.resume(),
    }, { textOnly: !recognizerAvailable });
    if (!recognizerAvailable) this.radioUI.showCantListenWarning('浏览器不支持');

    window.addEventListener('resize', this.onResize);
    this.mountUI();
    this.hud.showBanner('正在召集飞行员…', 0);
    this.bootstrapDialogue();
  }

  private async bootstrapDialogue(): Promise<void> {
    const refs = this.aiPlaneRefs();
    try {
      await this.dialogue.loadBackstories(refs);
    } catch (err) {
      console.warn('[bootstrap] backstory load error', err);
    }
    this.hud.showBanner('3v3 起飞！', 1500);
    // Stagger opening lines 0..2.5s so they don't pile up.
    refs.forEach((ref, i) => setTimeout(() => this.dialogue.fire(ref, 'opening'), 200 + i * 500));
    this.openingFired = true;
  }

  private aiPlaneRefs() {
    const refs = [];
    for (const e of this.planes) {
      if (!e.controller) continue;
      const c = e.controller;
      const plane = e.plane;
      refs.push({
        callsign: plane.callsign,
        team: plane.team,
        personalityId: c.personalityId,
        alive: () => plane.alive,
      });
    }
    return refs;
  }

  private onDialogueLine(line: DialogueLine): void {
    const back = this.dialogue.backstories.get(line.callsign);
    this.dialogueFeed.push(line, back?.name);
    // TTS playback. Sequential queue lives inside VoiceSynth.
    const personality = getPersonality(line.personalityId);
    this.voiceSynth.speak(line.text, personality);
  }

  private startListening(): void {
    this.voiceSynth.resume();   // R8: AudioContext gesture-resume
    if (!this.voiceRecognizer.available) return;
    this.voiceRecognizer.start({
      onResult: (text, _conf, isFinal) => {
        this.radioUI.showInterim(text);
        if (isFinal) {
          this.radioUI.hideInterim();
          this.playerRadio(text, 'all');
        }
      },
      onError: (err) => {
        this.radioUI.hideInterim();
        if (err === 'not-allowed' || err === 'service-not-allowed') {
          this.radioUI.showCantListenWarning('麦克风被拒');
        }
      },
      onEnd: () => this.radioUI.hideInterim(),
    });
  }

  private stopListening(): void {
    this.voiceRecognizer.stop();
  }

  private spawnPlayer(opts: GameAppOptions): Plane {
    const p = new Plane({
      shape: opts.shape,
      color: opts.color,
      team: 'blue',
      position: new THREE.Vector3(0, 30, -100),
      forward: new THREE.Vector3(0, 0, 1),
      isPlayer: true,
      callsign: 'PLAYER',
    });
    this.scene.add(p.group);
    this.planes.push({ plane: p });
    this.planeById.set(p.callsign, p);
    this.trails.attach(p);
    return p;
  }

  private spawnAI(team: Team, personalityId: PersonalityId, index: number, teamSize: number): void {
    const xSpread = team === 'blue' ? -1 : 1;
    const z = team === 'blue' ? -110 : 110;
    const slot = index - (teamSize - 1) / 2;
    const colors = COLORS_FOR_TEAM[team];
    const callsign = (team === 'blue' ? 'B' : 'R') + (index + 1) + '·' + personalityId.slice(0, 3).toUpperCase();
    const plane = new Plane({
      shape: SHAPES_FOR_AI[index % SHAPES_FOR_AI.length],
      color: colors[index % colors.length],
      team,
      position: new THREE.Vector3(slot * 12 * xSpread, 30 + slot * 4, z),
      forward: new THREE.Vector3(0, 0, team === 'blue' ? 1 : -1),
      callsign,
    });
    this.scene.add(plane.group);
    this.trails.attach(plane);
    const controller = new FullAIController(plane, personalityId, callsign);
    this.planes.push({ plane, controller });
    this.planeById.set(callsign, plane);
    this.controllerByCallsign.set(callsign, controller);
  }

  start(): void {
    this.clock.start();
    this.state.start(0);
    this.nowSeconds = 0;
    const tick = () => {
      const rawDt = this.clock.getDelta();
      const dt = Math.min(rawDt, 1 / 30);
      this.nowSeconds += dt;
      this.frame(dt);
      this.rafId = requestAnimationFrame(tick);
    };
    tick();
  }

  stop(): void {
    cancelAnimationFrame(this.rafId);
    window.removeEventListener('resize', this.onResize);
  }

  private frame(dt: number): void {
    this.state.step(this.nowSeconds);

    // Driving: collect inputs first, then advance physics, then collisions.
    this.input.tick(dt);

    const aliveList = this.planes.map((e) => e.plane);
    const fireHelper = (p: Plane) => this.fireFrom(p);

    if (this.player.alive) {
      this.player.applyInput(this.input.state, dt);
      if (this.input.consumeFirePulse() || this.input.state.fire) {
        this.fireFrom(this.player);
      }
    }

    for (const e of this.planes) {
      if (e === this.planes[0]) continue; // skip player (already done)
      if (!e.controller || !e.plane.alive) continue;
      const aiInput = e.controller.update(dt, { planes: aliveList, now: this.nowSeconds, fire: fireHelper });
      e.plane.applyInput(aiInput, dt);
      if (aiInput.firePulse) this.fireFrom(e.plane);
    }

    this.bullets.update(dt, aliveList, this.nowSeconds, this.planeById, (hit) => this.onHit(hit));
    this.fx.update(dt);
    this.trails.update(dt);

    if (this.player.alive) this.camera.follow(this.player, dt);
    else this.spectatorCam(dt);

    this.renderer.render(this.scene, this.camera.camera);
    this.updateHud();

    // Idle dialogue: 30s of silence -> someone chats.
    this.idleTimer += dt;
    if (this.openingFired && this.idleTimer > 30 && this.state.phase === 'live') {
      const speaker = pickRandomAlive(this.planes);
      if (speaker?.controller) {
        const ref = this.refForController(speaker.controller);
        if (ref) this.dialogue.fire(ref, 'idle');
      }
      this.idleTimer = 0;
    }

    if (this.state.phase === 'post' && !this.postScreen) {
      this.state.finalize(aliveList);
      this.showPostScreen();
    }
  }

  private spectatorCam(dt: number): void {
    // Pick the most aggressive surviving ally; fallback to any survivor.
    const survivor =
      this.planes.find((e) => e.plane.alive && e.plane.team === 'blue')?.plane ??
      this.planes.find((e) => e.plane.alive)?.plane;
    if (survivor) this.camera.follow(survivor, dt);
  }

  private fireFrom(p: Plane): void {
    if (!p.alive) return;
    if (!p.tryFire(this.nowSeconds)) return;
    const muzzle = p.position.clone().addScaledVector(p.forward, 1.4).addScaledVector(p.up, 0.05);
    const speed = 70 + p.velocity.length() * 0.4;
    const vel = p.forward.clone().multiplyScalar(speed).add(p.velocity.clone().multiplyScalar(0.4));
    this.bullets.spawn(new Bullet({ position: muzzle, velocity: vel, team: p.team, ownerId: p.callsign }));
  }

  private onHit(hit: { victim: Plane; killer?: Plane; killed: boolean }): void {
    if (!hit.killed) return;
    this.fx.spawn(hit.victim.position, this.colorForKillFx(hit.victim));
    this.trails.detach(hit.victim);
    this.scene.remove(hit.victim.group);
    this.killFeed.push({
      killer: hit.killer?.callsign,
      killerTeam: hit.killer?.team,
      victim: hit.victim.callsign,
      victimTeam: hit.victim.team,
    });
    this.state.recordKill({
      victim: hit.victim.callsign,
      victimTeam: hit.victim.team,
      killer: hit.killer?.callsign,
      killerTeam: hit.killer?.team,
      ts: this.nowSeconds,
    }, this.planes.map((e) => e.plane));
    if (hit.victim === this.player) this.hud.showBanner('你被击落了', 1600);

    // Dialogue triggers: killer celebrates (30%), victim's death (100% if AI),
    // surviving teammates may comment if low HP.
    const killerCtrl = hit.killer && this.controllerByCallsign.get(hit.killer.callsign);
    if (killerCtrl && hit.killer && Math.random() < 0.3) {
      const ref = this.refForController(killerCtrl);
      if (ref) {
        const victimBack = this.dialogue.backstories.get(hit.victim.callsign);
        this.dialogue.fire(ref, 'kill', { killVictimName: victimBack?.name ?? hit.victim.callsign });
      }
    }
    const victimCtrl = this.controllerByCallsign.get(hit.victim.callsign);
    if (victimCtrl) {
      const ref = this.refForController(victimCtrl);
      if (ref) this.dialogue.fire(ref, 'death');
    }
    // Ally low HP check: any surviving teammate of the victim with hp <= 1?
    if (hit.killer) {
      for (const e of this.planes) {
        if (!e.controller || !e.plane.alive) continue;
        if (e.plane.team !== hit.victim.team) continue;
        if (e.plane.hp > 1) continue;
        if (Math.random() > 0.5) continue;
        const ref = this.refForController(e.controller);
        if (ref) this.dialogue.fire(ref, 'ally_low');
      }
    }
  }

  private refForController(c: AIController) {
    const e = this.planes.find((x) => x.controller === c);
    if (!e) return null;
    return {
      callsign: e.plane.callsign,
      team: e.plane.team,
      personalityId: c.personalityId,
      alive: () => e.plane.alive,
    };
  }

  private colorForKillFx(plane: Plane): number {
    return plane.team === 'blue' ? 0xa8d8ff : 0xffaaa0;
  }

  private updateHud(): void {
    this.hud.update(this.player, this.state, this.planes.map((e) => e.plane));
    if (this.modeBadge) {
      const mode = this.input.mode;
      const label = mode === 'keyboard' ? '键鼠' : mode === 'joystick' ? '摇杆' : '陀螺仪';
      const speed = Math.round(this.player.velocity.length());
      this.modeBadge.textContent = `[${label}]  Speed ${speed} m/s`;
    }
    this.fpsLast += 1 / 60; // approximate; precise tracking below
    this.fpsFrames++;
    if (this.fpsLast >= 0.5) {
      const fps = Math.round(this.fpsFrames / this.fpsLast);
      this.fpsLast = 0; this.fpsFrames = 0;
      if (this.fpsCounter) this.fpsCounter.textContent = `FPS ${fps}`;
    }
  }

  private onResize = (): void => {
    this.renderer.setSize(window.innerWidth, window.innerHeight);
    this.camera.resize(window.innerWidth / window.innerHeight);
  };

  // -------- UI: mode badge + buttons + post screen --------

  private mountUI(): void {
    const badge = document.createElement('div');
    Object.assign(badge.style, {
      position: 'fixed', top: '12px', left: '12px', zIndex: '60',
      background: 'rgba(10,15,30,0.6)', border: '1px solid rgba(180,200,255,0.3)',
      padding: '6px 10px', borderRadius: '6px', color: '#cce',
      font: '12px/1.4 -apple-system, sans-serif', letterSpacing: '0.5px',
      pointerEvents: 'auto', userSelect: 'none',
    } as CSSStyleDeclaration);
    document.body.appendChild(badge);
    this.modeBadge = badge;

    const fps = document.createElement('div');
    Object.assign(fps.style, {
      position: 'fixed', top: '12px', right: '12px', zIndex: '60',
      background: 'rgba(10,15,30,0.6)', border: '1px solid rgba(180,200,255,0.2)',
      padding: '4px 8px', borderRadius: '6px', color: '#aac',
      font: '11px/1.4 ui-monospace, monospace', minWidth: '80px', textAlign: 'right',
    } as CSSStyleDeclaration);
    fps.textContent = 'FPS --';
    document.body.appendChild(fps);
    this.fpsCounter = fps;

    const modeRow = document.createElement('div');
    Object.assign(modeRow.style, {
      position: 'fixed', top: '54px', left: '12px', zIndex: '60', display: 'flex', gap: '6px',
    } as CSSStyleDeclaration);
    document.body.appendChild(modeRow);
    const mkBtn = (label: string, on: () => void): HTMLButtonElement => {
      const b = document.createElement('button');
      b.textContent = label;
      Object.assign(b.style, {
        background: 'rgba(20,30,55,0.7)', color: '#dde',
        border: '1px solid rgba(180,200,255,0.3)', padding: '4px 8px', borderRadius: '4px',
        font: '11px sans-serif', cursor: 'pointer',
      } as CSSStyleDeclaration);
      b.addEventListener('click', on);
      return b;
    };
    modeRow.appendChild(mkBtn('键鼠', () => this.setMode('keyboard')));
    modeRow.appendChild(mkBtn('摇杆', () => this.setMode('joystick')));
    modeRow.appendChild(mkBtn('陀螺仪', async () => {
      const ok = await this.input.enableGyro();
      if (!ok) this.flashMessage('陀螺仪不可用或被拒绝');
    }));
  }

  setMode(mode: InputMode): void {
    if (mode === 'keyboard') {
      this.input.destroyJoysticks();
      this.input.setMode('keyboard');
    } else {
      this.input.setMode(mode);
    }
  }

  private showPostScreen(): void {
    if (this.postScreen) return;
    const winner = this.state.winner;
    const text =
      winner === 'blue' ? '蓝方胜利' :
      winner === 'red' ? '红方胜利' :
      '平局';
    const overlay = document.createElement('div');
    Object.assign(overlay.style, {
      position: 'fixed', inset: '0', display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'rgba(10,12,30,0.7)', color: '#fff', zIndex: '90', flexDirection: 'column',
      font: '300 32px/1 -apple-system, sans-serif', letterSpacing: '4px',
    } as CSSStyleDeclaration);
    overlay.innerHTML = `<div style="margin-bottom:14px">${text}</div>
      <div style="font-size:14px;letter-spacing:2px;color:#aac">蓝 ${this.state.scoreBlue}  -  红 ${this.state.scoreRed}</div>`;
    const restart = document.createElement('button');
    restart.textContent = '再来一局';
    Object.assign(restart.style, {
      marginTop: '24px', padding: '8px 22px', border: '1px solid #88f', background: 'rgba(60,90,160,0.5)',
      color: '#fff', borderRadius: '20px', font: '14px sans-serif', letterSpacing: '2px', cursor: 'pointer',
    } as CSSStyleDeclaration);
    restart.addEventListener('click', () => location.reload());
    overlay.appendChild(restart);
    document.body.appendChild(overlay);
    this.postScreen = overlay;
  }

  /** Player radio: from PTT speech recognition or text input. */
  playerRadio(message: string, channel: 'friendly' | 'all' = 'all'): void {
    if (!message.trim()) return;
    this.idleTimer = 0;

    // Echo player line into the dialogue feed so the user sees what was heard.
    this.dialogueFeed.push({
      ts: Date.now(),
      callsign: 'PLAYER',
      team: this.player.team,
      personalityId: 'veteran', // arbitrary; only used for color in feed
      text: message,
      source: 'live',
      event: 'opening',
    } as DialogueLine, '你');

    // Pick at most 1 ally + 1 enemy responder (nearest of each) to avoid spam.
    const allies = this.planes.filter((e) => e.controller && e.plane.alive && e.plane.team === this.player.team);
    const enemies = this.planes.filter((e) => e.controller && e.plane.alive && e.plane.team !== this.player.team);
    const byDistance = (a: PlaneEntry, b: PlaneEntry) =>
      a.plane.position.distanceToSquared(this.player.position) - b.plane.position.distanceToSquared(this.player.position);
    allies.sort(byDistance);
    enemies.sort(byDistance);
    const responders: PlaneEntry[] = [];
    if (allies[0]) responders.push(allies[0]);
    if (channel === 'all' && enemies[0]) responders.push(enemies[0]);

    responders.forEach((e, i) => {
      if (!e.controller) return;
      const ref = this.refForController(e.controller);
      if (!ref) return;
      // Stagger by 250ms so two replies don't overlap on the wire.
      setTimeout(() => this.dialogue.fire(ref, 'player_radio', {
        playerMessage: message,
        enemyOrAlly: e.plane.team === this.player.team ? 'ally' : 'enemy',
      }), i * 250);
    });
  }

  private flashMessage(text: string): void {
    const m = document.createElement('div');
    Object.assign(m.style, {
      position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%,-50%)',
      background: 'rgba(60,20,30,0.9)', color: '#fdd',
      padding: '12px 18px', borderRadius: '8px', font: '14px sans-serif', zIndex: '100',
    } as CSSStyleDeclaration);
    m.textContent = text;
    document.body.appendChild(m);
    setTimeout(() => m.remove(), 2200);
  }
}
